'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, RotateCcw, Check, Type, Ruler, Box, MoveHorizontal, MoveVertical,
    AlignLeft, AlignCenter, AlignRight, Plus, Minus,
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useStyles } from '@/context/StyleContext';
import { useViewportZoom } from '@/lib/viewportZoom';
import { editorDrag } from '@/lib/editorDrag';

const FONT_WEIGHTS = ['300', '400', '500', '600', '700', '800', '900'];
const ALIGNMENTS = ['left', 'center', 'right'];

const inputClass =
    'w-full px-1.5 py-1 text-xs rounded border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary';

const toolBtnClass =
    'p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors';

function NumberField({
    label, value, onChange, min, max, step = 1, suffix = 'px',
}: {
    label: string;
    value?: string;
    onChange: (v: string) => void;
    min: number;
    max: number;
    step?: number;
    suffix?: string;
}) {
    const parsed = parseFloat(value ?? '');
    return (
        <label className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{label}</span>
            <input
                type="number"
                min={min}
                max={max}
                step={step}
                value={Number.isFinite(parsed) ? parsed : ''}
                onChange={(e) => onChange(e.target.value === '' ? '' : `${e.target.value}${suffix}`)}
                className={inputClass}
            />
        </label>
    );
}

function TextField({
    label, value, onChange, placeholder,
}: {
    label: string;
    value?: string;
    onChange: (v: string) => void;
    placeholder?: string;
}) {
    return (
        <label className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{label}</span>
            <input
                type="text"
                value={value ?? ''}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className={inputClass}
            />
        </label>
    );
}

function SelectField({
    label, value, onChange, options,
}: {
    label: string;
    value?: string;
    onChange: (v: string) => void;
    options: string[];
}) {
    return (
        <label className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{label}</span>
            <select value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputClass}>
                <option value="">Auto</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>
        </label>
    );
}

function ColorField({
    label, value, onChange,
}: {
    label: string;
    value?: string;
    onChange: (v: string) => void;
}) {
    const isHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value ?? '');
    return (
        <label className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground truncate">{label}</span>
            <div className="flex items-center gap-1">
                <input
                    type="color"
                    value={isHex ? value : '#000000'}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-6 w-8 p-0 rounded border border-border bg-background cursor-pointer"
                />
                {value && (
                    <button onClick={() => onChange('')} className="text-muted-foreground hover:text-destructive" title="Clear color">
                        <X className="w-3 h-3" />
                    </button>
                )}
            </div>
        </label>
    );
}

interface Rect { top: number; left: number; width: number; height: number }
interface ToolbarPos { top: number; left: number; below: boolean; caretX: number }

const rectEq = (a: Rect | null, b: Rect) =>
    !!a && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
const posEq = (a: ToolbarPos | null, b: ToolbarPos) =>
    !!a && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 && a.below === b.below && Math.abs(a.caretX - b.caretX) < 0.5;

const HANDLES: { key: string; h?: 'l' | 'r'; v?: 't' | 'b'; pos: string; cursor: string }[] = [
    { key: 'tl', h: 'l', v: 't', pos: 'top-0 left-0', cursor: 'cursor-nwse-resize' },
    { key: 'tr', h: 'r', v: 't', pos: 'top-0 left-full', cursor: 'cursor-nesw-resize' },
    { key: 'bl', h: 'l', v: 'b', pos: 'top-full left-0', cursor: 'cursor-nesw-resize' },
    { key: 'br', h: 'r', v: 'b', pos: 'top-full left-full', cursor: 'cursor-nwse-resize' },
    { key: 't', v: 't', pos: 'top-0 left-1/2', cursor: 'cursor-ns-resize' },
    { key: 'b', v: 'b', pos: 'top-full left-1/2', cursor: 'cursor-ns-resize' },
    { key: 'l', h: 'l', pos: 'top-1/2 left-0', cursor: 'cursor-ew-resize' },
    { key: 'r', h: 'r', pos: 'top-1/2 left-full', cursor: 'cursor-ew-resize' },
];

export default function StyleEditorFloating() {
    const { isAdmin } = useLanguage();
    const {
        editingStyleId, editorMode, anchorEl, closeStyleEditor,
        getRawStyle, updateStyle, resetStyle, saveStyle,
    } = useStyles();

    const toolbarRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const zoomRef = useViewportZoom();
    const closeTimer = useRef<number | null>(null);
    const dragging = useRef(false);
    const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number; below: boolean; caretX: number } | null>(null);
    const [union, setUnion] = useState<Rect | null>(null);
    const [openPopover, setOpenPopover] = useState<string | null>(null);
    const [chip, setChip] = useState<{ x: number; y: number; text: string } | null>(null);

    const active = !!isAdmin && !!editingStyleId && !!anchorEl;

    // Hover intent: the balloon stays open while the pointer is over the field,
    // toolbar or drag handles; leaving all of them closes it after a short grace period.
    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);
    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = window.setTimeout(() => closeStyleEditor(), 300);
    }, [cancelClose, closeStyleEditor]);

    // Keep toolbar + overlay glued to the anchor element. A continuous rAF loop
    // is used because entrance animations/transforms move the element without
    // firing scroll/resize/ResizeObserver events, which left stale rects.
    useEffect(() => {
        if (!active || !anchorEl) return;
        let raf = 0;
        let lastUnion: Rect | null = null;
        let lastPos: ToolbarPos | null = null;

        const measure = () => {
            if (!document.body.contains(anchorEl)) {
                closeStyleEditor();
                return;
            }
            // Union of the per-line fragments: hosts the drag handles and anchors
            // the toolbar. The visible border is painted by the element itself
            // (outline), exactly like the contentEditable focus ring.
            const domRects = anchorEl.getClientRects();
            const nextRects: Rect[] = [];
            for (let i = 0; i < domRects.length; i++) {
                const r = domRects[i];
                if (r.width < 1 && r.height < 1) continue;
                nextRects.push({ top: r.top, left: r.left, width: r.width, height: r.height });
            }
            if (nextRects.length === 0) {
                const r = anchorEl.getBoundingClientRect();
                nextRects.push({ top: r.top, left: r.left, width: r.width, height: r.height });
            }
            const bounds = nextRects.reduce(
                (acc, r) => ({
                    top: Math.min(acc.top, r.top),
                    left: Math.min(acc.left, r.left),
                    right: Math.max(acc.right, r.left + r.width),
                    bottom: Math.max(acc.bottom, r.top + r.height),
                }),
                { top: Infinity, left: Infinity, right: -Infinity, bottom: -Infinity },
            );
            const u: Rect = { top: bounds.top, left: bounds.left, width: bounds.right - bounds.left, height: bounds.bottom - bounds.top };
            // Reads (getClientRects, innerWidth) are viewport px, but the px we
            // WRITE into these fixed elements are interpreted in the zoomed
            // space of <html zoom> — divide every written value by z.
            const z = zoomRef.current;
            if (!rectEq(lastUnion, u)) {
                lastUnion = u;
                setUnion({ top: u.top / z, left: u.left / z, width: u.width / z, height: u.height / z });
            }

            const tb = toolbarRef.current;
            const tbH = (tb?.offsetHeight ?? 44) * z;
            const tbW = (tb?.offsetWidth ?? 340) * z;
            const below = u.top < tbH + 16;
            const top = below ? u.top + u.height + 10 : u.top - tbH - 10;
            let left = u.left + u.width / 2 - tbW / 2;
            left = Math.max(8, Math.min(left, window.innerWidth - tbW - 8));
            const caretX = Math.max(12, Math.min(u.left + u.width / 2 - left, tbW - 12));
            const nextPos: ToolbarPos = { top: top / z, left: left / z, below, caretX: caretX / z };
            if (!posEq(lastPos, nextPos)) {
                lastPos = nextPos;
                setToolbarPos(nextPos);
            }
            raf = requestAnimationFrame(measure);
        };

        raf = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(raf);
    }, [active, anchorEl, closeStyleEditor]);

    // Escape or pointerdown outside toolbar/overlay closes WITHOUT saving
    // (style mode only; text mode is closed by EditableContent on blur/Escape)
    useEffect(() => {
        if (!active || editorMode !== 'style') return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeStyleEditor();
        };
        const onDown = (e: PointerEvent) => {
            const t = e.target as Node;
            if (toolbarRef.current?.contains(t)) return;
            if (overlayRef.current?.contains(t)) return;
            closeStyleEditor();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onDown);
        };
    }, [active, editorMode, closeStyleEditor]);

    // Close when the pointer leaves the field, toolbar and handles
    useEffect(() => {
        if (!active || editorMode !== 'style') return;
        const onOver = (e: MouseEvent) => {
            if (dragging.current) return;
            const t = e.target as Node;
            if (anchorEl.contains(t) || toolbarRef.current?.contains(t) || overlayRef.current?.contains(t)) {
                cancelClose();
            } else {
                scheduleClose();
            }
        };
        document.addEventListener('mouseover', onOver);
        return () => {
            document.removeEventListener('mouseover', onOver);
            cancelClose();
        };
    }, [active, editorMode, anchorEl, cancelClose, scheduleClose]);

    if (!active || !editingStyleId || !anchorEl) return null;

    const id = editingStyleId;
    const raw = getRawStyle(id);
    const set = (patch: Record<string, string>) => updateStyle(id, patch);

    // Line spacing slider value (1.2 is only the visual default when unset)
    const lhRaw = parseFloat(raw.lineHeight ?? '');
    const lh = Number.isFinite(lhRaw) ? lhRaw : 1.2;

    // Viewport-safe scale: element-level CSS zoom multiplies each breakpoint's
    // responsive base size instead of pinning one absolute px everywhere.
    const scaleRaw = parseFloat(raw.zoom ?? '');
    const scale = Number.isFinite(scaleRaw) ? scaleRaw : 1;
    const bumpScale = (dir: 1 | -1) => {
        const next = Math.min(2, Math.max(0.5, Math.round((scale + dir * 0.1) * 100) / 100));
        set({ zoom: String(next) });
    };

    const clampMargin = (n: number) => Math.max(-100, Math.min(300, Math.round(n)));
    // Margins are stored as viewport-aware clamps: the tuned px wins on this
    // screen and wider ones, while narrower viewports shrink proportionally
    // instead of inheriting a desktop-sized gap (sign-aware: negatives shrink
    // toward 0 via max()).
    const toFluidMargin = (px: number) => {
        if (px === 0) return '0px';
        const vw = `${Math.round(Math.abs(px / window.innerWidth) * 10000) / 100}vw`;
        return px > 0 ? `min(${px}px, ${vw})` : `max(${px}px, -${vw})`;
    };
    const parseMargin = (v?: string) => {
        const m = /(-?\d+(?:\.\d+)?)px/.exec(v ?? '');
        return m ? parseFloat(m[1]) : 0;
    };
    const marginPx = (v?: string) => {
        const m = /(-?\d+(?:\.\d+)?)px/.exec(v ?? '');
        return m ? m[1] : '';
    };

    const startDrag = (e: React.PointerEvent, handle: (typeof HANDLES)[number]) => {
        e.preventDefault();
        e.stopPropagation();
        dragging.current = true;
        editorDrag.active = true;
        cancelClose();
        const z = zoomRef.current;
        const startX = e.clientX;
        const startY = e.clientY;
        const start = {
            l: parseMargin(raw.marginLeft),
            r: parseMargin(raw.marginRight),
            t: parseMargin(raw.marginTop),
            b: parseMargin(raw.marginBottom),
        };
        const onMove = (ev: PointerEvent) => {
            // Pointer deltas are viewport px; margins are written in zoomed px.
            const dx = (ev.clientX - startX) / z;
            const dy = (ev.clientY - startY) / z;
            const patch: Record<string, string> = {};
            if (handle.h === 'r') patch.marginRight = toFluidMargin(clampMargin(start.r - dx));
            if (handle.h === 'l') patch.marginLeft = toFluidMargin(clampMargin(start.l + dx));
            if (handle.v === 't') patch.marginTop = toFluidMargin(clampMargin(start.t + dy));
            if (handle.v === 'b') patch.marginBottom = toFluidMargin(clampMargin(start.b - dy));
            updateStyle(id, patch);
            const m = { ...start };
            if (handle.h === 'r') m.r = clampMargin(start.r - dx);
            if (handle.h === 'l') m.l = clampMargin(start.l + dx);
            if (handle.v === 't') m.t = clampMargin(start.t + dy);
            if (handle.v === 'b') m.b = clampMargin(start.b - dy);
            setChip({ x: ev.clientX / z, y: ev.clientY / z, text: `T ${m.t} · R ${m.r} · B ${m.b} · L ${m.l}` });
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            dragging.current = false;
            editorDrag.active = false;
            setChip(null);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const popoverCard = 'absolute top-full mt-2 left-0 p-3 rounded-lg border border-border bg-background/95 backdrop-blur-xl shadow-2xl space-y-2';

    return (
        <>
            {/* Union bounding box: invisible, hosts the drag handles */}
            {union && (
                <div
                    ref={overlayRef}
                    className="fixed z-[10000] pointer-events-none"
                    style={{ top: union.top, left: union.left, width: union.width, height: union.height }}
                >
                    {editorMode === 'style' && HANDLES.map((h) => (
                        <div
                            key={h.key}
                            onPointerDown={(e) => startDrag(e, h)}
                            className={`absolute ${h.pos} -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-sm bg-white border border-primary shadow pointer-events-auto ${h.cursor}`}
                            title="Drag to adjust margins"
                        />
                    ))}
                </div>
            )}

            {/* Floating balloon toolbar (style mode only) */}
            {editorMode === 'style' && (
            <div
                ref={toolbarRef}
                className="fixed z-[10001] flex items-center gap-0.5 px-1.5 py-1 rounded-lg border border-border bg-background/95 backdrop-blur-xl shadow-2xl"
                style={toolbarPos ? { top: toolbarPos.top, left: toolbarPos.left } : { top: -1000, left: -1000 }}
            >
                {toolbarPos && (
                    <div
                        className={`absolute w-2 h-2 rotate-45 bg-background border-border ${toolbarPos.below ? '-top-1 border-l border-t' : '-bottom-1 border-r border-b'}`}
                        style={{ left: toolbarPos.caretX - 4 }}
                    />
                )}

                <button
                    onClick={() => saveStyle(id)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-white bg-primary hover:bg-primary/90"
                    title="Save and reload"
                >
                    <Check className="w-3 h-3" />
                    Done
                </button>

                <div className="w-px h-5 bg-border mx-1" />

                <button className={`${toolBtnClass} ${raw.textAlign === 'left' ? 'text-foreground bg-muted' : ''}`} title="Align left" onClick={() => set({ textAlign: raw.textAlign === 'left' ? '' : 'left' })}>
                    <AlignLeft className="w-3.5 h-3.5" />
                </button>
                <button className={`${toolBtnClass} ${raw.textAlign === 'center' ? 'text-foreground bg-muted' : ''}`} title="Align center" onClick={() => set({ textAlign: raw.textAlign === 'center' ? '' : 'center' })}>
                    <AlignCenter className="w-3.5 h-3.5" />
                </button>
                <button className={`${toolBtnClass} ${raw.textAlign === 'right' ? 'text-foreground bg-muted' : ''}`} title="Align right" onClick={() => set({ textAlign: raw.textAlign === 'right' ? '' : 'right' })}>
                    <AlignRight className="w-3.5 h-3.5" />
                </button>

                <input
                    type="color"
                    value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw.color ?? '') ? raw.color : '#000000'}
                    onChange={(e) => set({ color: e.target.value })}
                    className="w-6 h-6 p-0 rounded border border-border bg-background cursor-pointer"
                    title="Text color"
                />

                <div className="w-px h-5 bg-border mx-1" />

                <div className="relative">
                    <button className={`${toolBtnClass} ${openPopover === 'text' ? 'text-foreground bg-muted' : ''}`} title="Typography" onClick={() => setOpenPopover(openPopover === 'text' ? null : 'text')}>
                        <Type className="w-3.5 h-3.5" />
                    </button>
                    {openPopover === 'text' && (
                        <div className={`${popoverCard} w-56`}>
                            <div className="grid grid-cols-2 gap-2">
                                <NumberField label="Font size" value={raw.fontSize} min={8} max={200} onChange={(v) => set({ fontSize: v })} />
                                <SelectField label="Weight" value={raw.fontWeight} options={FONT_WEIGHTS} onChange={(v) => set({ fontWeight: v })} />
                                <NumberField label="Letter sp." value={raw.letterSpacing} min={-5} max={50} step={0.5} onChange={(v) => set({ letterSpacing: v })} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button className={`${toolBtnClass} ${openPopover === 'lines' ? 'text-foreground bg-muted' : ''}`} title="Line spacing" onClick={() => setOpenPopover(openPopover === 'lines' ? null : 'lines')}>
                        <MoveVertical className="w-3.5 h-3.5" />
                    </button>
                    {openPopover === 'lines' && (
                        <div className={`${popoverCard} w-56`}>
                            <div className="flex items-center justify-between">
                                <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Line spacing</span>
                                <span className="text-[10px] font-bold text-foreground">{lh.toFixed(2)}x</span>
                            </div>
                            <input type="range" min={0.8} max={3} step={0.05} value={lh}
                                onChange={(e) => set({ lineHeight: e.target.value })} className="w-full accent-primary" />
                            <div className="flex items-end gap-2">
                                <NumberField label="Exact" value={raw.lineHeight} min={0.8} max={4} step={0.1} suffix="" onChange={(v) => set({ lineHeight: v })} />
                                <button className={toolBtnClass} title="Reset line spacing" onClick={() => set({ lineHeight: '' })}>
                                    <RotateCcw className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button className={`${toolBtnClass} ${openPopover === 'spacing' ? 'text-foreground bg-muted' : ''}`} title="Spacing" onClick={() => setOpenPopover(openPopover === 'spacing' ? null : 'spacing')}>
                        <Ruler className="w-3.5 h-3.5" />
                    </button>
                    {openPopover === 'spacing' && (
                        <div className={`${popoverCard} w-60`}>
                            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Margin (T R B L) — also draggable</div>
                            <div className="grid grid-cols-4 gap-1">
                                <NumberField label="T" value={marginPx(raw.marginTop)} min={-100} max={300} onChange={(v) => set({ marginTop: v === '' ? '' : toFluidMargin(clampMargin(parseFloat(v))) })} />
                                <NumberField label="R" value={marginPx(raw.marginRight)} min={-100} max={300} onChange={(v) => set({ marginRight: v === '' ? '' : toFluidMargin(clampMargin(parseFloat(v))) })} />
                                <NumberField label="B" value={marginPx(raw.marginBottom)} min={-100} max={300} onChange={(v) => set({ marginBottom: v === '' ? '' : toFluidMargin(clampMargin(parseFloat(v))) })} />
                                <NumberField label="L" value={marginPx(raw.marginLeft)} min={-100} max={300} onChange={(v) => set({ marginLeft: v === '' ? '' : toFluidMargin(clampMargin(parseFloat(v))) })} />
                            </div>
                            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Padding (T R B L)</div>
                            <div className="grid grid-cols-4 gap-1">
                                <NumberField label="T" value={raw.paddingTop} min={0} max={300} onChange={(v) => set({ paddingTop: v })} />
                                <NumberField label="R" value={raw.paddingRight} min={0} max={300} onChange={(v) => set({ paddingRight: v })} />
                                <NumberField label="B" value={raw.paddingBottom} min={0} max={300} onChange={(v) => set({ paddingBottom: v })} />
                                <NumberField label="L" value={raw.paddingLeft} min={0} max={300} onChange={(v) => set({ paddingLeft: v })} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button className={`${toolBtnClass} ${openPopover === 'border' ? 'text-foreground bg-muted' : ''}`} title="Border" onClick={() => setOpenPopover(openPopover === 'border' ? null : 'border')}>
                        <Box className="w-3.5 h-3.5" />
                    </button>
                    {openPopover === 'border' && (
                        <div className={`${popoverCard} w-56`}>
                            <div className="grid grid-cols-2 gap-2">
                                <NumberField label="Thickness" value={raw.borderWidth} min={0} max={20} onChange={(v) => set({ borderWidth: v })} />
                                <NumberField label="Radius" value={raw.borderRadius} min={0} max={100} onChange={(v) => set({ borderRadius: v })} />
                                <ColorField label="Border color" value={raw.borderColor} onChange={(v) => set({ borderColor: v })} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button className={`${toolBtnClass} ${openPopover === 'size' ? 'text-foreground bg-muted' : ''}`} title="Size" onClick={() => setOpenPopover(openPopover === 'size' ? null : 'size')}>
                        <MoveHorizontal className="w-3.5 h-3.5" />
                    </button>
                    {openPopover === 'size' && (
                        <div className={`${popoverCard} w-56`}>
                            <div>
                                <div className="text-[9px] uppercase tracking-wide text-muted-foreground mb-1">Scale (keeps responsive sizes)</div>
                                <div className="flex items-center justify-between gap-2">
                                    <button className={toolBtnClass} title="Decrease size" onClick={() => bumpScale(-1)}><Minus className="w-3.5 h-3.5" /></button>
                                    <span className="text-[10px] font-bold text-foreground w-10 text-center">{Math.round(scale * 100)}%</span>
                                    <button className={toolBtnClass} title="Increase size" onClick={() => bumpScale(1)}><Plus className="w-3.5 h-3.5" /></button>
                                    <button className={toolBtnClass} title="Reset scale" onClick={() => set({ zoom: '' })}><RotateCcw className="w-3 h-3" /></button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <NumberField label="Font size" value={raw.fontSize} min={8} max={200} onChange={(v) => set({ fontSize: v })} />
                                <TextField label="Width" value={raw.width} placeholder="auto / 320px / 100%" onChange={(v) => set({ width: v })} />
                                <TextField label="Max width" value={raw.maxWidth} placeholder="none / 600px" onChange={(v) => set({ maxWidth: v })} />
                            </div>
                            <div className="text-[9px] text-muted-foreground">Fixed px pins the size on all viewports; use -/+ scale to stay responsive.</div>
                        </div>
                    )}
                </div>

                <div className="w-px h-5 bg-border mx-1" />

                <button className={toolBtnClass} title="Reset styles (committed on Done)" onClick={() => resetStyle(id)}>
                    <RotateCcw className="w-3.5 h-3.5" />
                </button>
                <button className={toolBtnClass} title="Close without saving" onClick={closeStyleEditor}>
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
            )}

            {/* Live margin chip while dragging */}
            {chip && (
                <div
                    className="fixed z-[10002] px-1.5 py-0.5 rounded bg-primary text-white text-[10px] font-bold pointer-events-none whitespace-nowrap"
                    style={{ top: chip.y + 14, left: chip.x + 10 }}
                >
                    {chip.text}
                </div>
            )}
        </>
    );
}
